import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useId } from "react";

import type { HeroOverview as HeroOverviewData, ItemPurposeTag } from "../../model/HeroBuilds";
import { AbilityOrder } from "../AbilityOrder";
import { CoreItems } from "./CoreItems";
import { EnemyCounters } from "./EnemyCounters";
import { SituationalGroups } from "./SituationalGroups";

const TITLE = msg({ id: "builds.overview.title", message: "What players build on {hero}" });
const ORDER = msg({ id: "builds.details.order", message: "Skill order" });
const HOVER = msg({
  id: "builds.overview.hover_hint",
  message: "Hover over or tap an item or ability to see what it does and how often it is taken",
});

type HeroOverviewProps = {
  overview: HeroOverviewData;
  purposes: readonly ItemPurposeTag[];
  hero: string;
};

// Что собирают сильные на герое (доска «19д» `design/prototype/HeroOverview.dc.html`): по всем
// их матчам, а не по одной сборке, — основа по порядку, самая частая прокачка, предметы
// по ситуации и что берут против конкретных врагов. Ответ «что покупать» — сразу, без выбора
// сборки; сборки из игры — ниже, чтобы забрать это в игру.
export const HeroOverview = ({ overview, purposes, hero }: HeroOverviewProps) => {
  const { _ } = useLingui();
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-3.5">
      <h3 id={titleId} className="text-xl font-semibold">
        {_({ ...TITLE, values: { hero } })}
      </h3>
      {overview.core.length > 0 && <CoreItems items={overview.core} hero={hero} />}
      {overview.ability_order !== null && (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface px-4 py-3.5">
          <h4 className="text-base font-semibold">{_(ORDER)}</h4>
          <AbilityOrder order={overview.ability_order} scope="hero" hero={hero} showTitle={false} />
        </div>
      )}
      <SituationalGroups items={overview.situational} purposes={purposes} hero={hero} />
      <EnemyCounters counters={overview.counters} hero={hero} />
      <p className="text-xs text-subtle">{_(HOVER)}</p>
    </section>
  );
};
